'use client';

import { useEffect, useState } from 'react';

import { typewriterFrames } from './typewriterFrames';

const DEFAULT_TYPE_INTERVAL_MS = 90;
const DEFAULT_HOLD_MS = 1400;
const DEFAULT_ERASE_INTERVAL_MS = 45;

export type UseTypewriterOptions = {
  phrases: string[];
  prefix?: string;
  typeIntervalMs?: number;
  holdMs?: number;
  eraseIntervalMs?: number;
  // When false, the animation loop is not scheduled at all (no timers run). Use
  // this when the consumer discards the animated value — e.g. reduced-motion or a
  // non-empty input — so the hook does no idle work. Defaults to true.
  enabled?: boolean;
};

export const useTypewriter = ({
  phrases,
  prefix = '',
  typeIntervalMs = DEFAULT_TYPE_INTERVAL_MS,
  holdMs = DEFAULT_HOLD_MS,
  eraseIntervalMs = DEFAULT_ERASE_INTERVAL_MS,
  enabled = true,
}: UseTypewriterOptions): string => {
  const [frame, setFrame] = useState({ phraseIndex: 0, charCount: 0 });

  useEffect(() => {
    if (!enabled || phrases.length === 0) return;

    const gen = typewriterFrames(phrases, { typeIntervalMs, holdMs, eraseIntervalMs });
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tick = () => {
      const next = gen.next();
      if (next.done === true) return;
      setFrame({ phraseIndex: next.value.phraseIndex, charCount: next.value.charCount });
      timeoutId = setTimeout(tick, next.value.delayMs);
    };

    tick();

    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [enabled, phrases, typeIntervalMs, holdMs, eraseIntervalMs]);

  const current = phrases[frame.phraseIndex] ?? '';
  return `${prefix}${current.slice(0, frame.charCount)}`;
};
